import React, { FC, memo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { IncomeStatisticsFilterKeys } from '../../model/types';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { ApplicabilitySelect } from './ApplicabilitySelect';
import { WarehouseSelect } from './WarehouseSelect';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { ScaleSelect } from './ScaleSelect';

export const IncomeStatisticsFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<IncomeStatisticsFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: IncomeStatisticsFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: IncomeStatisticsFilterKeys.startDate,
        label: t('From {{dueDateFrom}}', { dueDateFrom: clientFormatDateFrom }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeEndDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: IncomeStatisticsFilterKeys.endDate,
        label: t('To {{dueDateTo}}', { dueDateTo: clientFormatDateTo }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeContractApplicability = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: IncomeStatisticsFilterKeys.isApplicable, label, value, title: t('Applicability') });
    },
    [changeFilters, t],
  );

  const changeInvoiceStatus = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: IncomeStatisticsFilterKeys.scale, label, value, title: t('Scale') });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Due date range')}
          onChangeFrom={changeStartDate}
          onChangeTo={changeEndDate}
          values={[filters.startDate?.value && dayjs(filters.startDate?.value), filters.endDate?.value && dayjs(filters.endDate?.value)]}
        />

        <div className="flex space-x-3 items-center">
          <ScaleSelect value={filters.scale?.value} onChange={changeInvoiceStatus} onClear={clearCurrentFilters} />
          <ApplicabilitySelect value={filters.isApplicable?.value} onChange={changeContractApplicability} onClear={clearCurrentFilters} />
        </div>
      </div>
    </TableFilter>
  );
});
